
import React from "react";
import { saveFile, getFile } from "../storage/blobStore";
import type { AttachmentMeta } from "../types/attachments";

export type FileMeta = AttachmentMeta;

type Props = {
  files: FileMeta[];
  onChange: (files: FileMeta[]) => void;
  onFileSelect?: (rawFiles: File[]) => void; // 新增：讓父層攔截檔案
  readOnly?: boolean;
};

export function FileUploader({ files = [], onChange, onFileSelect, readOnly }: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFiles = async (newFiles: FileList | null) => {
    if (!newFiles) return;
    
    // 如果父層有提供 onFileSelect，直接傳遞原始檔案，不走 IndexedDB
    if (onFileSelect) {
      const fileArray = Array.from(newFiles);
      onFileSelect(fileArray);
      // 清空 input 以便重複選取
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // 舊邏輯：存入 IndexedDB (BlobStore)
    const added: FileMeta[] = [];
    for (let i = 0; i < newFiles.length; i++) {
      const f = newFiles[i];
      try {
        const stored = await saveFile(f);
        added.push({ 
          blobId: stored.id, 
          name: stored.name, 
          size: stored.size, 
          type: stored.type,
          contentType: stored.type,
          url: "" 
        });
      } catch (e) {
        console.error("Upload failed", e);
        alert("上傳失敗: " + f.name);
      }
    }
    onChange([...files, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (readOnly) return;
    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    if (readOnly) return;
    const next = [...files];
    next.splice(index, 1);
    onChange(next);
  };

  const downloadFile = async (meta: FileMeta) => {
    try {
      // 優先檢查是否有 URL (Firebase Storage)
      if (meta.url) {
        window.open(meta.url, "_blank");
        return;
      }

      // 否則嘗試從 IndexedDB 讀取
      if (meta.blobId) {
        const f = await getFile(meta.blobId);
        if (!f) {
          alert("檔案已遺失或無法讀取");
          return;
        }
        const url = URL.createObjectURL(f.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = f.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        alert("無法下載：無效的檔案路徑");
      }
    } catch (e) {
      console.error(e);
      alert("下載失敗，請稍後再試");
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>附件 ({files.length})</div>
      
      {!readOnly && (
        <div 
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: "2px dashed var(--border)", borderRadius: 8, padding: 20,
            textAlign: "center", cursor: "pointer", color: "var(--muted)", fontSize: 13,
            background: "rgba(255,255,255,0.02)"
          }}
        >
          點擊選擇或拖曳檔案至此
          <input type="file" multiple ref={inputRef} style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
        </div>
      )}

      {files.length === 0 && readOnly && <div className="p">無附件</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
        {files.map((f, i) => (
          <div key={i} className="row" style={{ 
            background: "#222", padding: "6px 10px", borderRadius: 6, alignItems: "center", justifyContent: "space-between" 
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
              <span style={{ fontSize: 16 }}>{f.url ? "☁️" : "📄"}</span>
              <div style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span onClick={() => downloadFile(f)} style={{ cursor: "pointer", textDecoration: "underline", color: "#fff" }}>{f.name}</span>
                <span style={{ marginLeft: 8, color: "var(--muted)", fontSize: 11 }}>{((f.size || 0)/1024).toFixed(1)} KB</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={() => downloadFile(f)} 
                title="下載"
                style={{ background: "transparent", border: "none", color: "var(--ok)", cursor: "pointer", fontSize: 14 }}
              >
                ⬇
              </button>
              {!readOnly && (
                <button 
                  onClick={() => removeFile(i)}
                  title="刪除"
                  style={{ background: "transparent", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 16 }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
